import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/journeyApi";
import { JourneyTopBar, JourneyFooter } from "@/components/JourneyChrome";
import { PasswordField } from "@/components/PasswordField";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!token) return toast.error("This reset link is invalid.");
    if (password.length < 8) return toast.error("Use a password with at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (e) {
      toast.error(apiError(e, "Could not reset your password."));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#FAF8F4] text-[#171717]" data-testid="reset-page">
      <JourneyTopBar showSignIn={false} />
      <main className="flex flex-1 items-center justify-center px-5 py-12">
        <div className="w-full max-w-md">
          {!done ? (
            <div className="rounded-2xl pe-surface p-7">
              <h1 className="font-serif text-2xl text-[#171717]">Choose a new password</h1>
              <p className="mt-2 text-[15px] text-[#525252]">Enter a new password for your account.</p>
              <div className="mt-5">
                <PasswordField testid="reset-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (8+ characters)" autoComplete="new-password" />
              </div>
              <div className="mt-3">
                <PasswordField testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" autoComplete="new-password" />
              </div>
              <button
                type="button"
                onClick={submit}
                disabled={busy}
                data-testid="reset-submit"
                className="mt-4 inline-flex min-h-[52px] w-full items-center justify-center rounded-full bg-[#2F6B57] px-6 text-base font-semibold text-white transition-colors hover:bg-[#255544] disabled:opacity-60"
              >
                {busy ? "Saving…" : "Save new password"}
              </button>
            </div>
          ) : (
            <div className="rounded-2xl pe-surface p-7 text-center" data-testid="reset-done">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]">
                <CheckCircle2 className="h-7 w-7" />
              </span>
              <h1 className="mt-5 font-serif text-2xl text-[#171717]">Password updated</h1>
              <p className="mt-2 text-[15px] leading-relaxed text-[#525252]">You can now sign in with your new password.</p>
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true })}
                data-testid="reset-to-login"
                className="mt-5 inline-flex min-h-[48px] items-center justify-center rounded-full bg-[#2F6B57] px-7 text-sm font-semibold text-white transition-colors hover:bg-[#255544]"
              >
                Go to sign in
              </button>
            </div>
          )}
          <p className="mt-5 text-center text-xs text-[#8A7C6B]">
            <Link to="/login" className="font-semibold text-[#2F6B57] hover:underline">Back to sign in</Link>
          </p>
        </div>
      </main>
      <JourneyFooter />
    </div>
  );
}
